import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import Layout from "../components/layout/GSTlayout";
import { Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GSTComplianceReportPage = () => {
  // State for filtering
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Sample compliance data (replace with actual API data)
  const complianceData = {
    filingStatus: [
      { month: 'Jan', filed: 95, pending: 5, status: 'Completed' },
      { month: 'Feb', filed: 92, pending: 8, status: 'Pending' },
      { month: 'Mar', filed: 98, pending: 2, status: 'Completed' },
      { month: 'Apr', filed: 90, pending: 10, status: 'Pending' },
      { month: 'May', filed: 97, pending: 3, status: 'Completed' },
      { month: 'Jun', filed: 93, pending: 7, status: 'Pending' }
    ],
    complianceMetrics: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [{
        label: 'Compliance Percentage',
        data: [95, 92, 98, 90, 97, 93],
        backgroundColor: 'rgba(75, 192, 192, 0.6)'
      }]
    }
  };

  // Chart options
  const complianceChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Monthly GST Compliance Percentage' }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => value + '%'
        }
      }
    }
  };

  // Determine status chip color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed': return 'success';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Layout>
          <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        GST Compliance Report
      </Typography>

      {/* Filtering Controls */}
      <Box sx={{ display: 'flex', mb: 3, gap: 2 }}>
        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            label="Year"
          >
            {[2020, 2021, 2022, 2023, 2024].map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            label="Month"
          >
            {[
              { value: 1, label: 'January' },
              { value: 2, label: 'February' },
              { value: 3, label: 'March' },
              { value: 4, label: 'April' },
              { value: 5, label: 'May' },
              { value: 6, label: 'June' },
              { value: 7, label: 'July' },
              { value: 8, label: 'August' },
              { value: 9, label: 'September' },
              { value: 10, label: 'October' },
              { value: 11, label: 'November' },
              { value: 12, label: 'December' }
            ].map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Compliance Percentage Chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Bar 
              data={complianceData.complianceMetrics} 
              options={complianceChartOptions} 
            />
          </Paper>
        </Grid>

        {/* Quick Compliance Overview */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Compliance Overview
            </Typography>
            <Typography variant="body2">
              Total Returns Filed: 85
            </Typography>
            <Typography variant="body2">
              Pending Returns: 15
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Overall Compliance Rate: 85%
            </Typography>
          </Paper>
        </Grid>

        {/* Detailed Compliance Table */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Returns Filed</TableCell>
                    <TableCell align="right">Pending Returns</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {complianceData.filingStatus.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell component="th" scope="row">
                        {row.month}
                      </TableCell>
                      <TableCell align="right">{row.filed}</TableCell>
                      <TableCell align="right">{row.pending}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={row.status} 
                          color={getStatusColor(row.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>

    </Layout>
  
  );
};

export default GSTComplianceReportPage;